import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logo from '../../assets/ExamWarriorLogo.svg';

const ProfilePage = () => {
    const [childData, setChildData] = useState(null);
    const [parentData, setParentData] = useState(null);
    const [badges, setBadges] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchProfileData = async () => {
            try {
                const user = JSON.parse(localStorage.getItem('user'));
                const childId = user?.id || "";
                
                // Fetch child and parent data
                const [childRes, parentRes, badgesRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/profile/child/${childId}`),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/profile/parent/${childId}`),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/profile/badges/${childId}`)
                ]);

                setChildData(childRes.data);
                setParentData(parentRes.data);
                setBadges(badgesRes.data);
                setLoading(false);
            } catch (error) {
                toast.error("Failed to load profile data");
                console.error("Profile error:", error);
                setLoading(false);
            }
        };

        fetchProfileData();
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gradient-to-br from-blue-50 to-teal-50">
                <div className="loadingspinner">
                    <div id="square1" className="bg-orange-500"></div>
                    <div id="square2" className="bg-orange-400"></div>
                    <div id="square3" className="bg-orange-300"></div>
                    <div id="square4" className="bg-yellow-500"></div>
                    <div id="square5" className="bg-yellow-400"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-teal-50 pb-12">

            {/* Profile Section */}
            <div className="max-w-6xl mx-auto px-4 py-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Child Profile Card */}
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden col-span-1">
                        <div className="bg-orange-500 p-4 text-white">
                            <h2 className="text-2xl font-bold">Child Profile</h2>
                        </div>
                        <div className="p-6">
                            <div className="flex items-center mb-6">
                                <div className="bg-orange-100 p-4 rounded-full mr-4">
                                    <svg className="w-10 h-10 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold">{childData?.first_name} {childData?.last_name}</h3>
                                    <p className="text-gray-600">Grade {childData?.grade} </p>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <p className="text-sm text-gray-500">Username</p>
                                    <p className="font-medium">{childData?.username}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">School</p>
                                    <p className="font-medium">{childData?.school}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Joined On</p>
                                    <p className="font-medium">{new Date(childData?.created_at).toLocaleDateString()}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Parent Information Card */}
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden col-span-1">
                        <div className="bg-purple-500 p-4 text-white">
                            <h2 className="text-2xl font-bold">Parent Information</h2>
                        </div>
                        <div className="p-6">
                            <div className="flex items-center mb-6">
                                <div className="bg-purple-100 p-4 rounded-full mr-4">
                                    <svg className="w-10 h-10 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold">{parentData?.name}</h3>
                                    <p className="text-gray-600">Parent/Guardian</p>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <p className="text-sm text-gray-500">Email</p>
                                    <p className="font-medium">{parentData?.email}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Phone</p>
                                    <p className="font-medium">{parentData?.phone || 'Not provided'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Relationship</p>
                                    <p className="font-medium">{parentData?.relationship || 'Parent'}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Badges Section */}
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden col-span-1 md:col-span-1">
                        <div className="bg-blue-500 p-4 text-white">
                            <h2 className="text-2xl font-bold">Earned Badges</h2>
                        </div>
                        <div className="p-6">
                            {badges.length > 0 ? (
                                <div className="grid grid-cols-2 gap-4">
                                    {badges.map((badge, index) => (
                                        <div key={index} className="bg-blue-50 rounded-lg p-4 text-center">
                                            <div className="bg-blue-100 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-2">
                                                <svg className="w-8 h-8 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <h4 className="font-bold text-blue-800">{badge.category}</h4>
                                            <p className="text-sm text-gray-600">
                                                Earned on {new Date(badge.earned_at).toLocaleDateString()}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <p className="mt-4 text-gray-600">No badges earned yet</p>
                                    <button 
                                        onClick={() => navigate('/maths-topics')}
                                        className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition"
                                    >
                                        Start Earning Badges
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <ToastContainer />
        </div>
    );
};

export default ProfilePage;